import { useEffect, useState } from "react";
import axios from "axios";
import { baseURL, ALL_CLASSES, STUDENT_SUBJECTS } from "../../../Api/Api";
import "./Sections.css";

const api = axios.create({ baseURL });

/* ── Skeleton loaders ─────────────────────────────────────────────────── */

function SkeletonTabs() {
  return (
    <div className="skeleton-tabs">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="skeleton-tab" style={{ width: `${70 + i * 15}px` }} />
      ))}
    </div>
  );
}

function SkeletonCards() {
  return (
    <div className="loading-grid">
      {[1, 2, 3].map((i) => (
        <div key={i} className="skeleton-card">
          <div className="skeleton-img" style={{ height: "110px" }} />
          <div className="skeleton-body" style={{ paddingTop: "48px" }}>
            <div className="skeleton-line medium" style={{ margin: "0 auto 12px" }} />
            <div className="skeleton-line short"  style={{ margin: "0 auto 12px" }} />
            <div className="skeleton-line medium" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Subject card ─────────────────────────────────────────────────────── */

function SubjectCard({ subject }) {
  const imgSrc = subject.teacher_photo
    ? `${baseURL}/uploads/subjects/${subject.teacher_photo}`
    : null;
  const initial = subject.teacher_name?.trim()[0] || "أ";

  return (
    <div className="subject-card">
      <div className="subject-card-top">
        <div className="subject-card-avatar-wrap">
          {imgSrc ? (
            <img className="subject-card-avatar" src={imgSrc} alt={subject.teacher_name} />
          ) : (
            <div className="subject-card-avatar-fallback">{initial}</div>
          )}
        </div>
      </div>

      <div className="subject-card-body">
        <div className="subject-name">{subject.name}</div>
        <div className="subject-divider" />

        <div className="subject-info">
          <div className="subject-info-item">
            <svg className="subject-info-icon" width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
            </svg>
            <span>{subject.teacher_name}</span>
          </div>

          <div className="subject-info-item">
            <svg className="subject-info-icon" width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24 11.36 11.36 0 0 0 3.56.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1 11.36 11.36 0 0 0 .57 3.57 1 1 0 0 1-.25 1.02l-2.2 2.2z" />
            </svg>
            <span>{subject.teacher_phone}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Main section ─────────────────────────────────────────────────────── */

export default function SubjectsSection() {
  const [classes, setClasses]           = useState([]);
  const [classesLoading, setClassesLoading] = useState(true);

  const [activeClass, setActiveClass]   = useState(null);
  const [subjects, setSubjects]         = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);

  /* fetch all classes once */
  useEffect(() => {
    api.get(ALL_CLASSES)
      .then((res) => {
        const list = res.data?.classes || [];
        setClasses(list);
        if (list.length > 0) {
          setActiveClass(list[0]);
        }
      })
      .catch(() => setClasses([]))
      .finally(() => setClassesLoading(false));
  }, []);

  /* fetch subjects whenever active class changes */
  useEffect(() => {
    if (!activeClass) return;
    setSubjectsLoading(true);
    setSubjects([]);
    api.get(STUDENT_SUBJECTS(activeClass.id))
      .then((res) => setSubjects(res.data?.subjects || []))
      .catch(() => setSubjects([]))
      .finally(() => setSubjectsLoading(false));
  }, [activeClass]);

  return (
    <section className="section-wrapper white" id="materials">
      <div className="container">

        {/* Header */}
        <div className="section-header">
          <span className="section-badge badge-purple">المواد الدراسية</span>
          <h2 className="section-title">مواد وأساتذة متميزون</h2>
          <p className="section-subtitle">
            اختر صفك لاكتشاف المواد التعليمية والأساتذة المتاحين.
          </p>
          <div className="section-divider divider-purple" />
        </div>

        {/* Class tabs */}
        {classesLoading ? (
          <SkeletonTabs />
        ) : classes.length === 0 ? (
          <div className="empty-state">
            <svg width="64" height="64" fill="#9ca3af" viewBox="0 0 24 24">
              <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z" />
            </svg>
            <p>لا توجد صفوف متاحة حالياً</p>
          </div>
        ) : (
          <>
            <div className="class-tabs">
              {classes.map((cls) => (
                <button
                  key={cls.id}
                  className={`class-tab${activeClass?.id === cls.id ? " active" : ""}`}
                  onClick={() => setActiveClass(cls)}
                >
                  {cls.name}
                </button>
              ))}
            </div>

            {/* Subjects panel */}
            <div className="subjects-panel" key={activeClass?.id}>
              {activeClass && (
                <div className="subjects-class-label">
                  <span>مواد {activeClass.name}</span>
                </div>
              )}

              {subjectsLoading ? (
                <SkeletonCards />
              ) : subjects.length === 0 ? (
                <div className="empty-state">
                  <svg width="64" height="64" fill="#9ca3af" viewBox="0 0 24 24">
                    <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z" />
                  </svg>
                  <p>لا توجد مواد في هذا الصف حالياً</p>
                </div>
              ) : (
                <div className="cards-grid">
                  {subjects.map((subject) => (
                    <SubjectCard key={subject.id} subject={subject} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
